<template>
  <div class="simple-dynamic-data-table rounded-2 bg-base" :style="{ fontFamily: data?.theme?.font_family }">
    <div
      ref="tableContainer"
      class="w-full"
      :class="{
        'max-h-[calc(100vh-350px)] overflow-auto': !isPrintDisplayMode && !isScribbleChild,
        '!h-auto overflow-hidden': isPrintDisplayMode,
        'overflow-auto': isScribbleChild,
      }"
      :style="style"
    >
      <table class="w-full border-1 border-collapse" :class="{ 'row-layout': props.data?.layout === 'row' }">
        <template v-if="!!tableData.length && !loading && props.data?.layout !== 'row'">
          <TableHeader
            :columns="columns"
            :current-sorting="currentSorting"
            :header-background-color="headerBackgroundColor"
            :header-foreground-color="headerForegroundColor"
            @sort="handleSort"
          />
        </template>
        <tbody v-if="!!tableData.length && !loading">
          <tr
            v-for="(row, idx) in paginatedData"
            :key="row.key"
            :style="{
              borderBottom: `1px solid ${themeVars.primary6}`,
              backgroundColor: getRowBackgroundColor(idx, props.data, themeVars),
            }"
          >
            <td
              v-for="column in columns"
              :key="column.id"
              class="px-4 py-2 min-w-[100px] whitespace-normal"
              :data-property-cell="column.id === 'property' && props.data?.layout === 'row'"
              :style="{
                borderBottom: `1px solid ${themeVars.primary6}`,
                ...(column.id === 'property' && props.data?.layout === 'row'
                  ? {
                      backgroundColor: headerBackgroundColor,
                      color: headerForegroundColor,
                      fontWeight: 'bold',
                    }
                  : {}),
              }"
            >
              <template v-if="column.id === 'property' && props.data?.layout === 'row'">
                {{ getPropertyDisplayName(row[column.id]) }}
              </template>
              <template v-else>
                {{ formatValue(row[column.id], column.id) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!tableData.length" class="text-center p-4 text-primary3"></div>

    <div v-if="tableData.length && !hidePagination && !loading" class="flex items-center justify-end px-4 pt-4 gap-2">
      <div v-if="!hideTotalResults" class="font-500 tracking-[1.25px] color-primary2">
        {{ t('canvasUI.components.CTable.countResults', { count: tableData.length }) }}
      </div>
      <div v-if="!hidePageSelector && pageCount > 1" class="flex items-center gap-1">
        <CButton class="border-1 rounded w-[36px] h-[36px]" :disabled="currentPage === 1" @click="handlePreviousPage">
          <template #icon>
            <CIcon icon="angle-left" size="16px" />
          </template>
        </CButton>
        <div
          class="flex items-center px-2 gap-1 h-[36px] rounded font-size-[16px] tracking-[1.25px]"
          :style="{ backgroundColor: themeVars.primary6, color: themeVars.primary }"
        >
          {{ `${t('canvasUI.components.CTable.page')} ${currentPage} / ${pageCount}` }}
        </div>
        <CButton
          class="border-1 rounded w-[36px] h-[36px]"
          :disabled="currentPage === pageCount"
          @click="handleNextPage"
        >
          <template #icon>
            <CIcon icon="angle-right" size="16px" />
          </template>
        </CButton>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center pt-4 pb-4">
      <NSpin size="large" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSpin } from 'naive-ui'
import { type ColumnSort } from '@tanstack/table-core'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import TableHeader from './DynamicDataTable.raw.header.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import useDataTablePrint from '@canvas-builder/composables/useDataTablePrint'
import { ComponentTypes, type ComponentNode, type DynamicDataTableProps } from '@canvas-builder/types/canvas'
import { getRowBackgroundColor, formatCellValue } from './DynamicDataTable.utils'

interface Column {
  id: string
  header: string
  enableSorting: boolean
  sortDescFirst?: boolean
}

const props = defineProps<{
  data?: DynamicDataTableProps
  tableData: any[]
  columns: Column[]
  headerBackgroundColor: string
  headerForegroundColor: string
  initialSorting?: ColumnSort
  hidePageSelector?: boolean
  hidePagination?: boolean
  hideTotalResults?: boolean
  pageSize?: number
  parentType?: ComponentNode['type']
  style?: Record<string, any>
  loading?: boolean
}>()

const emit = defineEmits<{
  sortChanged: [s: ColumnSort | null]
}>()

const { t } = useI18n()
const { isPrintDisplayMode } = useCanvas()
const themeVars = useThemeVars()
const { applyRowPrintStyles, applyPropertyCellPrintStyles } = useDataTablePrint()

const tableContainer = ref<HTMLElement | null>(null)

// Sorting
const currentSorting = ref<ColumnSort | null>(props.initialSorting || null)

// Pagination
const currentPage = ref(1)
const pageCount = computed(() => {
  if (!props.pageSize || props.tableData.length <= props.pageSize) return 1
  return Math.ceil(props.tableData.length / props.pageSize)
})

const paginatedData = computed(() => {
  // Apply sorting
  const sortedData = [...props.tableData]
  if (currentSorting.value) {
    sortedData.sort((a, b) => {
      const valueA = a[currentSorting.value!.id]
      const valueB = b[currentSorting.value!.id]

      // Skip sorting for special cases
      if (a.isGroupHeader || b.isGroupHeader) return 0

      // Handle different value types
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return currentSorting.value!.desc ? valueB - valueA : valueA - valueB
      }

      const strA = String(valueA || '')
      const strB = String(valueB || '')
      return currentSorting.value!.desc ? strB.localeCompare(strA) : strA.localeCompare(strB)
    })
  }

  // Apply pagination
  if (props.pageSize) {
    const start = (currentPage.value - 1) * props.pageSize
    const end = start + props.pageSize
    return sortedData.slice(start, end)
  }

  return sortedData
})

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})

onMounted(() => {
  if (isPrintDisplayMode.value && tableContainer.value) {
    applyRowPrintStyles(tableContainer.value)

    if (props.data?.layout === 'row') {
      applyPropertyCellPrintStyles(tableContainer.value, props.headerBackgroundColor, props.headerForegroundColor)
    }
  }
})

function handleSort(columnId: string) {
  if (currentSorting.value?.id === columnId) {
    if (currentSorting.value.desc) {
      currentSorting.value = null
    } else {
      currentSorting.value = { id: columnId, desc: true }
    }
  } else {
    currentSorting.value = { id: columnId, desc: false }
  }

  emit('sortChanged', currentSorting.value)
  currentPage.value = 1 // Reset to first page on sort
}

function handlePreviousPage() {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

function handleNextPage() {
  if (currentPage.value < pageCount.value) {
    currentPage.value++
  }
}

function getPropertyDisplayName(propertyKey: string) {
  if (!props.data?.properties) return propertyKey

  const property = props.data.properties.find((prop) => prop.key === propertyKey)
  return property?.display_name || propertyKey
}

function formatValue(value: any, columnId: string): string {
  if (!props.data?.properties) {
    return value !== undefined ? String(value) : ''
  }

  const property = props.data.properties.find((prop) => prop.key === columnId)
  if (!property) {
    return value !== undefined ? String(value) : ''
  }

  return formatCellValue(value, property)
}
</script>

<style lang="scss" scoped>
.simple-dynamic-data-table {
  .row-layout {
    thead {
      display: none;
    }
  }
}
</style>
